import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { MathJax } from "better-react-mathjax";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import DefineInputGroup from "./DefineInputGroup";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import LoadingSvg from "../loadingSvg/LoadingSvg";

import {
    computeValidation,
    getChecked,
    getSecondSemChild,
    getSecondSemEo,
    getTouchedObject
} from "../../utils/SchoolAdmin/defineReportingPeriodUtils";
import { GlobalContext } from "../contextProvider/ContextProvider";
import { gradeTranslation } from "../../utils/SchoolAdmin/translations.jsx";

import "../../styles/DefineReportingPeriods.scss";

/**
 * This is the Define By Subject component for epr
 * Part of the define reporting periods functionality for school admin
 * @name DefineBySubject
 * @param {[]} combinedGrades
 * @param {bool} isApplicableGrade
 * @param {bool} isReleased
 * @param {string} selectedContentArea
 * @param {string} selectedGrade
 * @param {{}} selectedGradeObject
 * @param {func} setLabelObj
 * @param {func} setSelectedContentArea
 * @param {func} setSelectedGradeObject
 * @param {func} setSelectedLink
 * @param {{}} subject
 * @returns {JSX.Element}
 */
/**
 * Renders a single evidence outcome child row.
 */
const EoChildRow = ({
    changes,
    eoChild,
    gleKey,
    handleOnChange,
    isApplicableGrade,
    isSecondSemester,
    eoKey,
    secondSemesterProfileSelectedGrade,
    standardKey,
    subjectId,
    yearComplete
}) => {
    const { body, key, reportFlag, templateContentId } = eoChild;
    const firstSemChildChecked = getChecked(getTouchedObject(changes, templateContentId), eoChild);
    const secSemChild = getSecondSemChild(secondSemesterProfileSelectedGrade, standardKey, gleKey, eoKey, key);
    const secSemChildChecked = getChecked(getTouchedObject(changes, secSemChild.templateContentId), secSemChild);

    return (
        <li key={eoChild.key}>
            <div className="standard-row">
                {body && subjectId === 3 ? (
                    <MathJax>
                        <span className="eoChild-name" dangerouslySetInnerHTML={{ __html: body }} />
                    </MathJax>
                ) : (
                    body && <span className="eoChild-name" dangerouslySetInnerHTML={{ __html: body }} />
                )}
                {isApplicableGrade && (
                    <DefineInputGroup
                        firstSemChecked={firstSemChildChecked}
                        firstSemContentKey={key}
                        firstSemTemplateContentId={templateContentId}
                        handleOnChange={handleOnChange}
                        isSecondSemester={isSecondSemester}
                        reportFlag={reportFlag}
                        secondSemChecked={secSemChildChecked}
                        secondSemContentKey={secSemChild.key}
                        secondSemTemplateContentId={secSemChild.templateContentId}
                        yearComplete={yearComplete}
                    />
                )}
            </div>
        </li>
    );
};

EoChildRow.propTypes = {
    changes: PropTypes.instanceOf(Array),
    eoChild: PropTypes.oneOfType([PropTypes.object]),
    gleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleOnChange: PropTypes.func,
    isApplicableGrade: PropTypes.bool,
    isSecondSemester: PropTypes.bool,
    eoKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondSemesterProfileSelectedGrade: PropTypes.oneOfType([PropTypes.object]),
    standardKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subjectId: PropTypes.number,
    yearComplete: PropTypes.bool
};

/**
 * Renders a single evidence outcome row with its children.
 */
const EoRow = ({
    changes,
    eo,
    gleKey,
    handleOnChange,
    isApplicableGrade,
    isSecondSemester,
    secondSemesterProfileSelectedGrade,
    standardKey,
    subjectId,
    yearComplete
}) => {
    const secSemEo = getSecondSemEo(secondSemesterProfileSelectedGrade, standardKey, gleKey, eo.key);
    const firstSemEoChecked = getChecked(getTouchedObject(changes, eo.templateContentId), eo);
    const secSemEoChecked = getChecked(getTouchedObject(changes, secSemEo.templateContentId), secSemEo);

    return (
        <li key={eo.key}>
            <div className="standard-row">
                {eo?.body && subjectId === 3 ? (
                    <MathJax>
                        <span className="eo-name" dangerouslySetInnerHTML={{ __html: eo.body }} />
                    </MathJax>
                ) : (
                    eo?.body && <span className="eo-name" dangerouslySetInnerHTML={{ __html: eo.body }} />
                )}
                {isApplicableGrade && (
                    <DefineInputGroup
                        firstSemChecked={firstSemEoChecked}
                        firstSemContentKey={eo.key}
                        firstSemTemplateContentId={eo.templateContentId}
                        handleOnChange={handleOnChange}
                        isSecondSemester={isSecondSemester}
                        reportFlag={eo.reportFlag}
                        secondSemChecked={secSemEoChecked}
                        secondSemContentKey={secSemEo.key}
                        secondSemTemplateContentId={secSemEo.templateContentId}
                        yearComplete={yearComplete}
                    />
                )}
            </div>
            <ul>
                {eo.evidenceOutcomeChildList?.map((eoChild) => (
                    <EoChildRow
                        key={eoChild.key}
                        changes={changes}
                        eoChild={eoChild}
                        eoKey={eo.key}
                        gleKey={gleKey}
                        handleOnChange={handleOnChange}
                        isApplicableGrade={isApplicableGrade}
                        isSecondSemester={isSecondSemester}
                        secondSemesterProfileSelectedGrade={secondSemesterProfileSelectedGrade}
                        standardKey={standardKey}
                        subjectId={subjectId}
                        yearComplete={yearComplete}
                    />
                ))}
            </ul>
        </li>
    );
};

EoRow.propTypes = {
    changes: PropTypes.instanceOf(Array),
    eo: PropTypes.oneOfType([PropTypes.object]),
    gleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleOnChange: PropTypes.func,
    isApplicableGrade: PropTypes.bool,
    isSecondSemester: PropTypes.bool,
    secondSemesterProfileSelectedGrade: PropTypes.oneOfType([PropTypes.object]),
    standardKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subjectId: PropTypes.number,
    yearComplete: PropTypes.bool
};

const DefineBySubject = ({
    combinedGrades,
    isApplicableGrade,
    isReleased,
    selectedContentArea,
    selectedGrade,
    selectedGradeObject,
    setLabelObj,
    setSelectedContentArea,
    setSelectedGradeObject,
    setSelectedLink,
    setTemplateTouchedFlag,
    subject: { cdeStandards, completed, subjectId, subjectName, profileSubjectId }
}) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { currentTimePeriodDto, token, userDetails } = state;

    const navigate = useNavigate();

    const [changes, setChanges] = useState([]);
    const [loader, setLoader] = useState(false);
    const [open, setOpen] = useState("false");
    const [existingRatingInfo, setExistingRatingInfo] = useState([]);
    const secondSemesterProfiles = selectedGradeObject.semesterProfiles.secondSem.subjectToStandards;
    const secondSemesterProfileSelectedGrade = secondSemesterProfiles.find((secSub) => secSub.subjectId === subjectId);
    const yearComplete = completed === true && secondSemesterProfileSelectedGrade.completed === true;
    const isSecondSemester = currentTimePeriodDto.name === "2nd Semester";

    const handleOnChange = (event) => {
        const tempChanges = [...changes];
        const { name, value } = event.target;
        const previousVal = value !== "false";
        const prevChange = tempChanges.some((prevChange) => prevChange.key === name);
        const changedObj = { key: name, included: !previousVal };

        if (prevChange) {
            const filteredChanges = tempChanges.filter((obj) => {
                return obj.key !== name;
            });
            setChanges(filteredChanges);
        } else {
            setChanges([...changes, changedObj]);
        }
    };

    const handleOnClick = (linkName) => {
        const array = linkName.split("/").map((str) => {
            return { label: str, path: `/${str}` };
        });
        setSelectedLink(linkName);
        setLabelObj(array);
        navigate(`/${linkName.split(" ")[0].toLowerCase()}`);
    };

    const handleComplete = (currentCompletedState, firstSemProfileSubjectId, secondSemProfileSubjectId) => {
        if (isReleased) {
            setOpen("confirm-unrelease");
            return;
        }
        if (changes.length > 0) {
            handleSaveAndComplete(currentCompletedState, firstSemProfileSubjectId, secondSemProfileSubjectId);
            return;
        }
        handleCompleteBothSemesters(currentCompletedState, firstSemProfileSubjectId, secondSemProfileSubjectId);
    };

    const handleUpdateFlags = (isFirstTime = true) => {
        const options = {
            action: "updateSchoolTemplateIncludedFlag",
            params: {
                lastUpdaterGuid: userDetails.uid,
                isFirstTime
            },
            data: changes,
            token
        };
        setLoader(true);

        ElementaryProgressReportDao(options)
            .then((response) => {
                const payload = response?.data?.payload ?? [];

                if (!response?.errors && Array.isArray(payload) && payload.length === 0) {
                    toast.success(`Reporting periods changes saved successfully.`, { autoClose: 5000 });
                    setChanges([]);
                    dispatch({ type: "SchoolLevelTemplateDto", schoolLevelTemplateDto: null });
                    setTemplateTouchedFlag(true);
                    setExistingRatingInfo([]);
                    return;
                }

                if (!response?.errors && Array.isArray(payload) && payload.length > 0) {
                    setExistingRatingInfo(payload);
                    setTimeout(() => setOpen("confirm-remove"), 0);
                    return;
                }

                toast.error(`Reporting periods changes could not be saved.`, { autoClose: false });
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    const handleSaveAndComplete = (currentCompletedState, firstSemProfileSubjectId, secondSemProfileSubjectId) => {
        const updateFlagOptions = {
            action: "updateSchoolTemplateIncludedFlag",
            params: {
                lastUpdaterGuid: userDetails.uid,
                isFirstTime: true
            },
            data: changes,
            token
        };
        ElementaryProgressReportDao(updateFlagOptions)
            .then((response) => {
                const payload = response?.data?.payload ?? [];
                if (!response?.errors && Array.isArray(payload) && payload.length === 0) {
                    toast.success(`Reporting periods changes saved successfully.`, {
                        autoClose: 5000
                    });
                    handleCompleteBothSemesters(
                        currentCompletedState,
                        firstSemProfileSubjectId,
                        secondSemProfileSubjectId
                    );
                    return;
                }
                if (!response?.errors && Array.isArray(payload) && payload.length > 0) {
                    setExistingRatingInfo(payload);
                    setTimeout(() => setOpen("confirm-remove"), 0);
                }
            })
            .catch((error) => {
                toast.error(`Reporting periods changes could not be saved.`, { autoClose: false });
                console.error(error);
            });
    };

    const handleCompleteBothSemesters = async (
        currentCompletedState,
        firstSemProfileSubjectId,
        secondSemProfileSubjectId
    ) => {
        setLoader(true);
        const firstSemProfileGradeId = selectedGradeObject.semesterProfiles.firstSem.profileGradeId;
        const secondSemProfileGradeId = selectedGradeObject.semesterProfiles.secondSem.profileGradeId;
        const gradeDisplayName = selectedGrade === "K" ? "kindergarten" : `${gradeTranslation[selectedGrade]} grade`;

        try {
            const firstSemResponse = await ElementaryProgressReportDao({
                action: "completeSubjectGradeTemplate",
                guid: userDetails.uid,
                profileGradeId: firstSemProfileGradeId,
                profileSubjectIds: `${firstSemProfileSubjectId}`,
                isCompleted: !currentCompletedState,
                token
            });

            if (!firstSemResponse) return;

            const secondSemResponse = await ElementaryProgressReportDao({
                action: "completeSubjectGradeTemplate",
                guid: userDetails.uid,
                profileGradeId: secondSemProfileGradeId,
                profileSubjectIds: `${secondSemProfileSubjectId}`,
                isCompleted: !currentCompletedState,
                token
            });

            if (!secondSemResponse) return;

            toast.success(
                currentCompletedState === true
                    ? `Successfully marked ${selectedContentArea} for ${gradeDisplayName} as incomplete.`
                    : `${selectedContentArea} for ${gradeDisplayName} successfully marked complete.`,
                { autoClose: 3000 }
            );
            setSelectedContentArea("");
            setChanges([]);
            dispatch({ type: "SchoolLevelTemplateDto", schoolLevelTemplateDto: null });
            setTemplateTouchedFlag(true);
            setTimeout(() => setLoader(false), 1000);
        } catch (error) {
            console.error(error);
            setLoader(false);
        }
    };

    /**
     * Get the selected grades data
     */
    useEffect(() => {
        if (combinedGrades && selectedGrade) {
            const selectedGradeObject = combinedGrades.find((grade) => grade.grade === selectedGrade);
            setSelectedGradeObject(selectedGradeObject);
        }
    }, [combinedGrades, selectedGrade, setSelectedGradeObject]);

    /**
     * Scroll to clicked content area
     */
    useEffect(() => {
        if (selectedContentArea) {
            const element = document.getElementById(selectedContentArea);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [selectedContentArea]);

    /**
     * Validation rules:
     * 1. Every reportable item must be checked in at least one semester.
     * 2. At least one reportable item must appear in 1st semester AND at least
     *    one must appear in 2nd semester (can't assign everything to one semester).
     *
     * Returns:
     *   uncheckedKeys  — Set of content keys where both semesters are unchecked (rule 1 violations)
     *   hasFirstSem    — true if any reportable item is included in 1st semester
     *   hasSecondSem   — true if any reportable item is included in 2nd semester
     *   valid          — true when all rules pass
     */
    const validation = useMemo(
        () => computeValidation(cdeStandards, secondSemesterProfileSelectedGrade, changes),
        [cdeStandards, changes, secondSemesterProfileSelectedGrade]
    );

    const { uncheckedKeys, hasFirstSem, hasSecondSem, validForComplete } = validation;

    const addedCount = changes.filter((i) => i.included).length;
    const removedAttemptCount = changes.filter((i) => i.included === false).length;

    const impactedIds = Array.from(new Set((existingRatingInfo || []).map((d) => d.templateContentId)));
    const impactedCount = impactedIds.length;

    const safeRemovedCount = Math.max(removedAttemptCount - impactedCount, 0);

    const hasAdds = addedCount > 0;
    const hasSafeRemovals = safeRemovedCount > 0;
    return (
        <div key={subjectId}>
            <button
                id={subjectName}
                onClick={() => {
                    if (selectedContentArea !== subjectName) {
                        setSelectedContentArea(subjectName);
                    } else {
                        setSelectedContentArea("");
                    }
                }}
                className={selectedContentArea !== subjectName ? "subject-name" : "selected-subject-name"}
            >
                {subjectName}
                <div className="complete-centered-tag">
                    {isApplicableGrade && (
                        <div className={`secondary-text-define ${yearComplete ? "complete-text-green" : ""}`}>
                            {yearComplete ? "Completed" : "Incomplete"}
                        </div>
                    )}
                    <i className={selectedContentArea !== subjectName ? "bi bi-chevron-down" : "bi bi-chevron-up"}></i>
                </div>
            </button>
            {selectedContentArea === subjectName && cdeStandards.length === 0 && (
                <div className="open-content-area">
                    <div className="header-define-table">
                        <div className="standard-row">
                            <span className="no-reportable-content-message">
                                No reportable content defined on district template for this subject.
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {selectedContentArea === subjectName && cdeStandards.length > 0 && (
                <div className="open-content-area">
                    <div className="header-define-table">
                        <div className="standard-row">
                            <span className="subject-name-header">{subjectName} - Reportable Content</span>
                            {isApplicableGrade && (
                                <div className="standard-check-container">
                                    <div className="header">1st</div>
                                    <div className="header">2nd</div>
                                </div>
                            )}
                        </div>
                    </div>
                    {cdeStandards.map((standard, index) => {
                        const firstSemStdTouched = getTouchedObject(changes, standard.templateContentId);
                        const firstSemStdChecked = getChecked(firstSemStdTouched, standard);

                        const secSemStd = secondSemesterProfileSelectedGrade.cdeStandards.find(
                            (std) => std.gradeToContentKey === standard.gradeToContentKey
                        );
                        const secSemStdTouched = getTouchedObject(changes, secSemStd.templateContentId);
                        const secSemStdChecked = getChecked(secSemStdTouched, secSemStd);
                        const displayStandardName = standard.standardName.substring(
                            standard.standardName.indexOf(".") + 1
                        );

                        return (
                            <div key={standard.key}>
                                <div className="standard-row std">
                                    <span className="standard-name-define">
                                        Standard {index + 1}: {displayStandardName}
                                    </span>
                                    {isApplicableGrade && (
                                        <DefineInputGroup
                                            firstSemChecked={firstSemStdChecked}
                                            firstSemContentKey={standard.gradeToContentKey}
                                            firstSemTemplateContentId={standard.templateContentId}
                                            handleOnChange={handleOnChange}
                                            isSecondSemester={isSecondSemester}
                                            reportFlag={standard.reportFlag}
                                            secondSemChecked={secSemStdChecked}
                                            secondSemContentKey={secSemStd.gradeToContentKey}
                                            secondSemTemplateContentId={secSemStd.templateContentId}
                                            yearComplete={yearComplete}
                                        />
                                    )}
                                </div>
                                {standard.gles.map((gle) => {
                                    const firstSemGleTouched = getTouchedObject(changes, gle.templateContentId);
                                    const firstSemGleChecked = getChecked(firstSemGleTouched, gle);

                                    const secSemGle = secondSemesterProfileSelectedGrade.cdeStandards
                                        .find((std) => std.key === standard.key)
                                        .gles.find((semTwoGle) => semTwoGle.key === gle.key);

                                    const secSemGleTouched = getTouchedObject(changes, secSemGle.templateContentId);

                                    const secSemGleChecked = getChecked(secSemGleTouched, secSemGle);
                                    const displayGleName = gle.gleName.substring(gle.gleName.indexOf(".") + 1);

                                    return (
                                        <div key={gle.key} style={{ marginLeft: "40px" }}>
                                            <div className="standard-row">
                                                <span className="gle-name">
                                                    {gle.gleShortName}: {displayGleName}
                                                </span>
                                                {isApplicableGrade && (
                                                    <DefineInputGroup
                                                        firstSemChecked={firstSemGleChecked}
                                                        firstSemContentKey={gle.key}
                                                        firstSemTemplateContentId={gle.templateContentId}
                                                        handleOnChange={handleOnChange}
                                                        isSecondSemester={isSecondSemester}
                                                        reportFlag={gle.reportFlag}
                                                        secondSemChecked={secSemGleChecked}
                                                        secondSemContentKey={secSemGle.key}
                                                        secondSemTemplateContentId={secSemGle.templateContentId}
                                                        yearComplete={yearComplete}
                                                    />
                                                )}
                                            </div>
                                            <ul style={{ marginLeft: "40px" }}>
                                                {gle.evidenceOutcomes?.map((eo) => (
                                                    <EoRow
                                                        key={eo.key}
                                                        changes={changes}
                                                        eo={eo}
                                                        gleKey={gle.key}
                                                        handleOnChange={handleOnChange}
                                                        isApplicableGrade={isApplicableGrade}
                                                        isSecondSemester={isSecondSemester}
                                                        secondSemesterProfileSelectedGrade={
                                                            secondSemesterProfileSelectedGrade
                                                        }
                                                        standardKey={standard.key}
                                                        subjectId={subjectId}
                                                        yearComplete={yearComplete}
                                                    />
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {isApplicableGrade && (
                        <div className="profile-button-container">
                            {!yearComplete && uncheckedKeys.size > 0 && (
                                <div className="validation-error-banner">
                                    Please assign each content item for reporting in either 1st Semester or 2nd
                                    Semester. You may also designate a content item for reporting in both semesters.
                                </div>
                            )}
                            {!yearComplete && hasFirstSem && !hasSecondSem && (
                                <div className="validation-warning-banner">
                                    <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: "6px" }} /> No
                                    content items assigned to 2nd Semester. Before completing, please review and confirm
                                    this is your intent.
                                </div>
                            )}
                            {!yearComplete && hasSecondSem && !hasFirstSem && (
                                <div className="validation-warning-banner">
                                    <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: "6px" }} /> No
                                    content items assigned to 1st Semester. Before completing, please review and confirm
                                    this is your intent.
                                </div>
                            )}
                            <ActionButton
                                className="action-button-cancel"
                                disable={yearComplete || changes.length === 0}
                                label="Cancel Changes"
                                onClick={() => {
                                    setChanges([]);
                                }}
                            />
                            <ActionButton
                                className="action-button-reg"
                                disable={yearComplete || changes.length === 0}
                                label="Save"
                                onClick={() => handleUpdateFlags(true)}
                            />
                            <div className="define-complete-check-wrapper">
                                <label
                                    className={`define-complete-check-btn ${!validForComplete ? "disabled-save-and-complete" : ""}`}
                                    disabled={!validForComplete}
                                >
                                    <input
                                        type="checkbox"
                                        checked={yearComplete}
                                        disabled={!validForComplete}
                                        onChange={() =>
                                            handleComplete(
                                                yearComplete,
                                                profileSubjectId,
                                                secondSemesterProfileSelectedGrade.profileSubjectId
                                            )
                                        }
                                    />
                                    <span>{yearComplete ? "Completed" : "Save and Complete"}</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {loader && <LoadingSvg />}
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => {
                                setOpen("false");
                                setChanges([]);
                                dispatch({ type: "SchoolLevelTemplateDto", schoolLevelTemplateDto: null });
                                setTemplateTouchedFlag(true);
                                setExistingRatingInfo([]);
                            }}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm and Remove"
                            onClick={() => {
                                handleUpdateFlags(false);
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Save Remove Dialog"
                hasCloseX={false}
                id="confirm-remove"
                open={open}
                title={<span className="remove-dialog-title">Manage School Template Summary</span>}
            >
                <div>
                    {hasAdds && (
                        <div className="success-section-wrapper">
                            <span className="success-title-text">Content Item(s) Added</span>
                            <div className="success-count-text">
                                You successfully added {addedCount} content item{addedCount !== 1 ? "s" : ""}.
                            </div>
                        </div>
                    )}
                    {hasSafeRemovals && (
                        <div className="success-section-wrapper">
                            <b className="success-title-text">Content Item(s) Removed</b>
                            <div className="success-count-text">
                                You successfully removed {safeRemovedCount} content item
                                {safeRemovedCount !== 1 ? "s" : ""} with no impact to ratings/comments for teachers.
                            </div>
                        </div>
                    )}
                    {(hasSafeRemovals || hasAdds) && <hr />}
                    <div className="attention-required-title">⚠️ ATTENTION REQUIRED</div>
                    <div className="attention-required-text">
                        You are attempting to remove{" "}
                        <b>
                            {impactedCount} content item{impactedCount !== 1 ? "s" : ""}{" "}
                        </b>
                        that {impactedCount !== 1 ? "are" : "is"} already in use by teachers. Removing{" "}
                        {impactedCount !== 1 ? "these" : "this"} content item{impactedCount !== 1 ? "s" : ""} will
                        delete ratings and comments.
                    </div>
                    <div className="affected-teachers-title">Affected Teachers:</div>
                    <ul className="affected-teachers-list">
                        {existingRatingInfo &&
                            existingRatingInfo.length > 0 &&
                            existingRatingInfo.map((dto) => {
                                const {
                                    contentBody,
                                    ratingCount,
                                    ratingCommentCount,
                                    teacherGuid,
                                    teacherName,
                                    templateContentId
                                } = dto;
                                return (
                                    <li key={`${teacherGuid}-${templateContentId}`} style={{ marginBottom: "6px" }}>
                                        <span>
                                            <b>{teacherName}</b> has <b>{ratingCount} ratings</b> and{" "}
                                            <b>{ratingCommentCount} rating comment(s)</b> for:{" "}
                                            <div className="affected-teacher-bullet">
                                                <MathJax>
                                                    <span dangerouslySetInnerHTML={{ __html: contentBody }} />
                                                </MathJax>
                                            </div>
                                        </span>
                                    </li>
                                );
                            })}
                    </ul>
                    <div className="remove-dialog-imp-wrapper">
                        <span className="remove-dialog-imp-text">
                            Please confirm that you wish to proceed, and acknowledge that you have notified affected
                            teachers. Please note that after saving the changes, you will need to click Save and
                            Complete for the content area before you can release to teachers. If you do not wish to
                            proceed with these changes, click Cancel to return to template management.
                        </span>
                    </div>
                </div>
            </DcsdDialog>
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm"
                            onClick={() => {
                                handleOnClick("HOME/TEMPLATE");
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Unrelease Dialog"
                hasCloseX={false}
                id="confirm-unrelease"
                open={open}
                title={<span className="unrelease-dialog-title">Manage Reporting Period(s) Alert</span>}
            >
                <div>
                    In order to make changes to the reporting period for one or more content areas, you must first
                    unrelease the grade-level template.{" "}
                    <span className="unrelease-dialog-imp-text">
                        Click confirm to return to the manage school template page.
                    </span>
                </div>
            </DcsdDialog>
        </div>
    );
};

DefineBySubject.propTypes = {
    combinedGrades: PropTypes.instanceOf(Array),
    isApplicableGrade: PropTypes.bool,
    isReleased: PropTypes.bool,
    selectedContentArea: PropTypes.string,
    selectedGrade: PropTypes.string,
    selectedGradeObject: PropTypes.oneOfType([PropTypes.object]),
    setLabelObj: PropTypes.func,
    setSelectedContentArea: PropTypes.func,
    setSelectedGradeObject: PropTypes.func,
    setSelectedLink: PropTypes.func,
    setTemplateTouchedFlag: PropTypes.func,
    subject: PropTypes.oneOfType([PropTypes.object])
};

export default DefineBySubject;
